import React, { useState, useEffect } from 'react';
import {
  AIAgent
} from '../../../types/aiAgent';
import { clickable } from "components/ui/interactive";

interface ImpactMetric {
  id: string;
  name: string;
  category: 'performance' | 'efficiency' | 'quality' | 'business';
  value: number;
  previousValue: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  attribution: number; // 0-1, how much agent contributed
  confidence: number;
  timeframe: string;
}

interface BusinessImpact {
  id: string;
  type: 'revenue' | 'cost_savings' | 'efficiency' | 'quality';
  description: string;
  value: number;
  currency?: string;
  unit?: string;
  percentage?: number;
  timeframe: string;
  confidence: number;
  attribution: {
    agentId: string;
    contribution: number;
    decisions: string[];
  }[];
}

interface AIAgentImpactTrackerProps {
  agent: AIAgent;
  timeRange?: '24h' | '7d' | '30d' | '90d' | '1y';
  onMetricSelect?: (metric: ImpactMetric) => void;
  onImpactSelect?: (impact: BusinessImpact) => void;
}

export const AIAgentImpactTracker: React.FC<AIAgentImpactTrackerProps> = ({
  agent,
  timeRange = '30d',
  onMetricSelect,
  onImpactSelect
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'attribution' | 'trends' | 'business'>('overview');
  const [impactMetrics, setImpactMetrics] = useState<ImpactMetric[]>([]);
  const [businessImpacts, setBusinessImpacts] = useState<BusinessImpact[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadImpactData();
  }, [agent.id, timeRange]);

  const loadImpactData = async () => {
    try {
      setLoading(true);
      // TODO: Load impact metrics from API
      // const metrics = await aiAgentService.getImpactMetrics(agent.id, timeRange);
      // const impacts = await aiAgentService.getBusinessImpacts(agent.id, timeRange);
      
      setImpactMetrics([]);
      setBusinessImpacts([]);
    } catch (error) {
      console.error('Failed to load impact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    const icons = {
      up: '📈',
      down: '📉',
      stable: '➡️'
    };
    return icons[trend];
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable', isPositive: boolean = true) => {
    if (trend === 'stable') return 'text-content-secondary';
    const isGoodTrend = (trend === 'up' && isPositive) || (trend === 'down' && !isPositive);
    return isGoodTrend ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300';
  };

  const getAttributionColor = (attribution: number) => {
    if (attribution >= 0.8) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (attribution >= 0.6) return 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30';
    if (attribution >= 0.4) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const formatCurrency = (value: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const formatNumber = (value: number, unit: string) => {
    if (unit === '%') return `${value.toFixed(1)}%`;
    if (unit === 'power') return value.toFixed(2);
    if (unit === 'days') return `${value.toFixed(1)} ${unit}`;
    return `${value.toLocaleString()} ${unit}`;
  };

  const calculateTotalImpact = () => {
    const revenueImpact = businessImpacts
      .filter(impact => impact.type === 'revenue')
      .reduce((sum, impact) => sum + (impact.value * (impact.attribution.find(a => a.agentId === agent.id)?.contribution || 0)), 0);
    
    const costSavings = businessImpacts
      .filter(impact => impact.type === 'cost_savings')
      .reduce((sum, impact) => sum + (impact.value * (impact.attribution.find(a => a.agentId === agent.id)?.contribution || 0)), 0);
    
    return { revenueImpact, costSavings, totalImpact: revenueImpact + costSavings };
  };

  const totalImpact = calculateTotalImpact();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">
              Impact Tracker - {agent.name}
            </h3>
            <p className="text-sm text-content-secondary mt-1">
              Performance attribution and business impact analysis
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold text-green-600 dark:text-green-300">
              {formatCurrency(totalImpact.totalImpact)}
            </div>
            <div className="text-sm text-content-secondary">Total Impact ({timeRange})</div>
          </div>
        </div>

        {/* Impact Summary */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3">
            <div className="text-sm text-green-600 dark:text-green-300">Revenue Impact</div>
            <div className="text-lg font-semibold text-green-700 dark:text-green-300">
              {formatCurrency(totalImpact.revenueImpact)}
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
            <div className="text-sm text-blue-600 dark:text-blue-300">Cost Savings</div>
            <div className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              {formatCurrency(totalImpact.costSavings)}
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3">
            <div className="text-sm text-purple-600 dark:text-purple-300">Avg Attribution</div>
            <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
              {Math.round(impactMetrics.reduce((sum, m) => sum + m.attribution, 0) / impactMetrics.length * 100)}%
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3">
            <div className="text-sm text-orange-600 dark:text-orange-300">Avg Confidence</div>
            <div className="text-lg font-semibold text-orange-700 dark:text-orange-300">
              {Math.round(impactMetrics.reduce((sum, m) => sum + m.confidence, 0) / impactMetrics.length * 100)}%
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'metrics', label: 'Performance Metrics', icon: '📈' },
            { id: 'attribution', label: 'Attribution Analysis', icon: '🎯' },
            { id: 'trends', label: 'Impact Trends', icon: '📉' },
            { id: 'business', label: 'Business Impact', icon: '💰' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <ImpactOverviewTab
            metrics={impactMetrics}
            businessImpacts={businessImpacts}
            agent={agent}
            onMetricSelect={onMetricSelect}
          />
        )}

        {activeTab === 'metrics' && (
          <PerformanceMetricsTab
            metrics={impactMetrics}
            onMetricSelect={onMetricSelect}
            getTrendIcon={getTrendIcon}
            getTrendColor={getTrendColor}
            getAttributionColor={getAttributionColor}
            formatNumber={formatNumber}
          />
        )}

        {activeTab === 'attribution' && (
          <AttributionAnalysisTab
            metrics={impactMetrics}
            businessImpacts={businessImpacts}
            agent={agent}
            getAttributionColor={getAttributionColor}
          />
        )}

        {activeTab === 'trends' && (
          <ImpactTrendsTab
            metrics={impactMetrics}
            timeRange={timeRange}
            getTrendIcon={getTrendIcon}
            getTrendColor={getTrendColor}
            formatNumber={formatNumber}
          />
        )}

        {activeTab === 'business' && (
          <BusinessImpactTab
            businessImpacts={businessImpacts}
            agent={agent}
            onImpactSelect={onImpactSelect}
            formatCurrency={formatCurrency}
            getAttributionColor={getAttributionColor}
          />
        )}
      </div>
    </div>
  );
};

// Impact Overview Tab
const ImpactOverviewTab: React.FC<{
  metrics: ImpactMetric[];
  businessImpacts: BusinessImpact[];
  agent: AIAgent;
  onMetricSelect?: (metric: ImpactMetric) => void;
}> = ({ metrics, businessImpacts, agent, onMetricSelect }) => {
  const topMetrics = metrics.sort((a, b) => b.attribution - a.attribution).slice(0, 3);
  const topImpacts = businessImpacts.sort((a, b) => b.value - a.value).slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Top Performing Metrics */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Top Impact Areas</h4>
        <div className="grid grid-cols-3 gap-4">
          {topMetrics.map(metric => (
            <div
              key={metric.id}
              className="border border-line rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
              {...clickable(() => onMetricSelect?.(metric))}
            >
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-content">{metric.name}</h5>
                <span className="text-2xl">
                  {metric.trend === 'up' ? '📈' : metric.trend === 'down' ? '📉' : '➡️'}
                </span>
              </div>
              <div className="text-2xl font-bold text-content dark:text-gray-100 mb-1">
                {metric.unit === '%' ? `${metric.value.toFixed(1)}%` : 
                 metric.unit === 'power' ? metric.value.toFixed(2) :
                 `${metric.value.toLocaleString()} ${metric.unit}`}
              </div>
              <div className="text-sm text-content-secondary dark:text-gray-400 mb-2">
                Attribution: {Math.round(metric.attribution * 100)}%
              </div>
              <div className="text-xs text-content-muted">
                Confidence: {Math.round(metric.confidence * 100)}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Impact Summary */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Business Impact Summary</h4>
        <div className="space-y-3">
          {topImpacts.map(impact => {
            const agentAttribution = impact.attribution.find(a => a.agentId === agent.id);
            const attributedValue = impact.value * (agentAttribution?.contribution || 0);
            
            return (
              <div key={impact.id} className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-content">{impact.description}</h5>
                  <div className="text-right">
                    <div className="font-bold text-content">
                      {impact.currency ? 
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: impact.currency }).format(attributedValue) :
                        `${attributedValue.toLocaleString()} ${impact.unit || ''}`
                      }
                    </div>
                    <div className="text-sm text-content-secondary">
                      {Math.round((agentAttribution?.contribution || 0) * 100)}% attributed
                    </div>
                  </div>
                </div>
                <div className="text-sm text-content-secondary">
                  Confidence: {Math.round(impact.confidence * 100)}% • Timeframe: {impact.timeframe}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agent Performance Summary */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Agent Performance Summary</h4>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                {Math.round(agent.performance.overall.successRate * 100)}%
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-300">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                {agent.performance.overall.tasksCompleted}
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-300">Tasks Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                {Math.round(agent.performance.overall.improvementRate * 100)}%
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-300">Improvement Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                {Math.round(agent.performance.overall.averageConfidence * 100)}%
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-300">Avg Confidence</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Performance Metrics Tab
const PerformanceMetricsTab: React.FC<{
  metrics: ImpactMetric[];
  onMetricSelect?: (metric: ImpactMetric) => void;
  getTrendIcon: (trend: 'up' | 'down' | 'stable') => string;
  getTrendColor: (trend: 'up' | 'down' | 'stable', isPositive?: boolean) => string;
  getAttributionColor: (attribution: number) => string;
  formatNumber: (value: number, unit: string) => string;
}> = ({
  metrics,
  onMetricSelect,
  getTrendIcon,
  getTrendColor,
  getAttributionColor,
  formatNumber
}) => {
  const categorizedMetrics = metrics.reduce((acc, metric) => {
    if (!acc[metric.category]) acc[metric.category] = [];
    acc[metric.category].push(metric);
    return acc;
  }, {} as Record<string, ImpactMetric[]>);

  return (
    <div className="space-y-6">
      {Object.entries(categorizedMetrics).map(([category, categoryMetrics]) => (
        <div key={category}>
          <h4 className="text-lg font-semibold text-content mb-4 capitalize">
            {category} Metrics
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {categoryMetrics.map(metric => (
              <div
                key={metric.id}
                className="border border-line rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                {...clickable(() => onMetricSelect?.(metric))}
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-content">{metric.name}</h5>
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">{getTrendIcon(metric.trend)}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAttributionColor(metric.attribution)}`}>
                      {Math.round(metric.attribution * 100)}%
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-content-secondary">Current</span>
                    <span className="font-semibold text-content">
                      {formatNumber(metric.value, metric.unit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-content-secondary">Previous</span>
                    <span className="text-gray-700 dark:text-gray-300">
                      {formatNumber(metric.previousValue, metric.unit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-content-secondary">Change</span>
                    <span className={getTrendColor(metric.trend, metric.name !== 'Time to Significance')}>
                      {metric.unit === '%' ? 
                        `${(metric.value - metric.previousValue).toFixed(1)}pp` :
                        `${((metric.value - metric.previousValue) / metric.previousValue * 100).toFixed(1)}%`
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-content-secondary">Confidence</span>
                    <span className="text-gray-700 dark:text-gray-300">
                      {Math.round(metric.confidence * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Attribution Analysis Tab
const AttributionAnalysisTab: React.FC<{
  metrics: ImpactMetric[];
  businessImpacts: BusinessImpact[];
  agent: AIAgent;
  getAttributionColor: (attribution: number) => string;
}> = ({ metrics, businessImpacts, agent, getAttributionColor }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Attribution Breakdown</h4>
        <p className="text-sm text-content-secondary mb-6">
          How much of each improvement can be attributed to this agent's decisions and actions.
        </p>
        
        <div className="space-y-4">
          {metrics.map(metric => (
            <div key={metric.id} className="border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-content">{metric.name}</h5>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getAttributionColor(metric.attribution)}`}>
                  {Math.round(metric.attribution * 100)}% Attribution
                </span>
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-3 mb-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${metric.attribution * 100}%` }}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-content-secondary">Agent Contribution:</span>
                  <span className="ml-2 font-medium">
                    {Math.round(metric.attribution * 100)}%
                  </span>
                </div>
                <div>
                  <span className="text-content-secondary">Other Factors:</span>
                  <span className="ml-2 font-medium">
                    {Math.round((1 - metric.attribution) * 100)}%
                  </span>
                </div>
                <div>
                  <span className="text-content-secondary">Confidence:</span>
                  <span className="ml-2 font-medium">
                    {Math.round(metric.confidence * 100)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Business Impact Attribution</h4>
        <div className="space-y-4">
          {businessImpacts.map(impact => {
            const agentAttribution = impact.attribution.find(a => a.agentId === agent.id);
            if (!agentAttribution) return null;
            
            return (
              <div key={impact.id} className="border border-line rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-content">{impact.description}</h5>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getAttributionColor(agentAttribution.contribution)}`}>
                    {Math.round(agentAttribution.contribution * 100)}% Attribution
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-3 mb-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${agentAttribution.contribution * 100}%` }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <span className="text-content-secondary">Total Impact:</span>
                    <span className="ml-2 font-medium">
                      {impact.currency ? 
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: impact.currency }).format(impact.value) :
                        `${impact.value.toLocaleString()} ${impact.unit || ''}`
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-content-secondary">Agent's Share:</span>
                    <span className="ml-2 font-medium">
                      {impact.currency ? 
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: impact.currency }).format(impact.value * agentAttribution.contribution) :
                        `${(impact.value * agentAttribution.contribution).toLocaleString()} ${impact.unit || ''}`
                      }
                    </span>
                  </div>
                </div>
                
                <div className="text-sm text-content-secondary">
                  <span>Related Decisions: </span>
                  <span className="font-medium">{agentAttribution.decisions.length} decisions</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Impact Trends Tab
const ImpactTrendsTab: React.FC<{
  metrics: ImpactMetric[];
  timeRange: string;
  getTrendIcon: (trend: 'up' | 'down' | 'stable') => string;
  getTrendColor: (trend: 'up' | 'down' | 'stable', isPositive?: boolean) => string;
  formatNumber: (value: number, unit: string) => string;
}> = ({ metrics, timeRange, getTrendIcon, getTrendColor, formatNumber }) => {
  // Mock trend data
  const generateTrendData = (metric: ImpactMetric) => {
    const points = [];
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      // Generate trend based on metric trend
      let value = metric.previousValue;
      if (metric.trend === 'up') {
        value += (metric.value - metric.previousValue) * (1 - i / days);
      } else if (metric.trend === 'down') {
        value += (metric.value - metric.previousValue) * (1 - i / days);
      }
      
      points.push({
        date,
        value: value + (Math.random() - 0.5) * value * 0.1 // Add some noise
      });
    }
    
    return points;
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Impact Trends Over Time</h4>
        <p className="text-sm text-content-secondary mb-6">
          How metrics have changed over the selected time period.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {metrics.map(metric => {
          const trendData = generateTrendData(metric);
          const isPositiveTrend = metric.name !== 'Time to Significance';
          
          return (
            <div key={metric.id} className="border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-medium text-content">{metric.name}</h5>
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{getTrendIcon(metric.trend)}</span>
                  <span className={`text-sm font-medium ${getTrendColor(metric.trend, isPositiveTrend)}`}>
                    {metric.trend === 'up' ? '+' : metric.trend === 'down' ? '-' : ''}
                    {Math.abs(((metric.value - metric.previousValue) / metric.previousValue * 100)).toFixed(1)}%
                  </span>
                </div>
              </div>
              
              {/* Simple trend visualization */}
              <div className="space-y-2 mb-4">
                {trendData.slice(-10).map((point, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-content-secondary w-20">
                      {point.date.toLocaleDateString()}
                    </span>
                    <div className="flex-1 mx-3">
                      <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            metric.trend === 'up' ? 'bg-green-500' :
                            metric.trend === 'down' ? 'bg-red-500' :
                            'bg-gray-500'
                          }`}
                          style={{ 
                            width: `${Math.min(Math.max((point.value / Math.max(...trendData.map(p => p.value))) * 100, 10), 100)}%` 
                          }}
                        />
                      </div>
                    </div>
                    <span className="font-medium w-16 text-right">
                      {formatNumber(point.value, metric.unit)}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="text-xs text-content-muted text-center">
                Attribution: {Math.round(metric.attribution * 100)}% • 
                Confidence: {Math.round(metric.confidence * 100)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Business Impact Tab
const BusinessImpactTab: React.FC<{
  businessImpacts: BusinessImpact[];
  agent: AIAgent;
  onImpactSelect?: (impact: BusinessImpact) => void;
  formatCurrency: (value: number, currency?: string) => string;
  getAttributionColor: (attribution: number) => string;
}> = ({ businessImpacts, agent, onImpactSelect, formatCurrency, getAttributionColor }) => {
  const getImpactIcon = (type: BusinessImpact['type']) => {
    const icons = {
      revenue: '💰',
      cost_savings: '💸',
      efficiency: '⚡',
      quality: '⭐'
    };
    return icons[type];
  };

  const totalValue = businessImpacts.reduce((sum, impact) => {
    const agentAttribution = impact.attribution.find(a => a.agentId === agent.id);
    return sum + (impact.value * (agentAttribution?.contribution || 0));
  }, 0);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Business Impact Analysis</h4>
        <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-700 dark:text-green-300">
              {formatCurrency(totalValue)}
            </div>
            <div className="text-sm text-green-600 dark:text-green-300">
              Total Attributed Business Impact
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {businessImpacts.map(impact => {
          const agentAttribution = impact.attribution.find(a => a.agentId === agent.id);
          if (!agentAttribution) return null;
          
          const attributedValue = impact.value * agentAttribution.contribution;
          
          return (
            <div
              key={impact.id}
              className="border border-line rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow"
              {...clickable(() => onImpactSelect?.(impact))}
            >
              <div className="flex items-start space-x-4">
                <div className="text-3xl">{getImpactIcon(impact.type)}</div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-content">{impact.description}</h5>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getAttributionColor(agentAttribution.contribution)}`}>
                      {Math.round(agentAttribution.contribution * 100)}% Attribution
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-content-secondary">Total Impact</div>
                      <div className="font-semibold text-content">
                        {impact.currency ? formatCurrency(impact.value, impact.currency) : 
                         `${impact.value.toLocaleString()} ${impact.unit || ''}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-content-secondary">Agent's Share</div>
                      <div className="font-semibold text-green-600 dark:text-green-300">
                        {impact.currency ? formatCurrency(attributedValue, impact.currency) : 
                         `${attributedValue.toLocaleString()} ${impact.unit || ''}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-content-secondary">Confidence</div>
                      <div className="font-semibold text-content">
                        {Math.round(impact.confidence * 100)}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-content-secondary">
                    <span>Timeframe: {impact.timeframe}</span>
                    <span>Related Decisions: {agentAttribution.decisions.length}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};